<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { Guest } from '@/types/guest';

interface Props {
  guest: Guest;
  isVisible: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const handleClose = (): void => {
  emit('close');
};

const handleClickOutside = (event: MouseEvent): void => {
  const target = event.target as HTMLElement;
  if (target.classList.contains('additional-questions-popup')) {
    handleClose();
  }
};

const handleEscapeKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    handleClose();
  }
};

const getStatusClass = (status: boolean | undefined) => {
  if (status === true) return 'additional-questions-popup__status--confirmed';
  if (status === false) return 'additional-questions-popup__status--declined';
  return 'additional-questions-popup__status--pending';
};

const getStatusIcon = (status: boolean | undefined) => {
  if (status === true) return 'pi pi-check-circle text-green-500';
  if (status === false) return 'pi pi-times-circle text-red-500';
  return 'pi pi-clock text-yellow-500';
};

const getStatusText = (status: boolean | undefined) => {
  if (status === true) return 'Potwierdzono';
  if (status === false) return 'Odmówiono';
  return 'Oczekuje';
};

const isAnswerEmpty = (answer: string | number | boolean | (string | number | boolean)[] | undefined): boolean => {
  return answer === '' || answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0);
};

const questionsWithAnswers = computed(() => {
  return props.guest.additionalQuestions.filter((question) => !isAnswerEmpty(question.answer));
});

const formatAnswer = (answer: string | number | boolean | (string | number | boolean)[] | undefined): string => {
  if (answer === undefined || answer === null) return 'Brak odpowiedzi';
  if (typeof answer === 'boolean') return answer ? 'Tak' : 'Nie';
  if (Array.isArray(answer)) return answer.join(', ');
  return String(answer);
};

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});
</script>

<template>
  <div
    v-if="isVisible"
    class="additional-questions-popup"
    role="dialog"
    aria-modal="true"
    aria-labelledby="popup-title"
    @click="handleClickOutside"
  >
    <div class="additional-questions-popup__content">
      <div class="additional-questions-popup__header">
        <h3
          id="popup-title"
          class="additional-questions-popup__title"
        >
          Szczegóły gościa
        </h3>
        <button
          class="additional-questions-popup__close-button"
          aria-label="Zamknij okno"
          @click="handleClose"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="additional-questions-popup__guest-info">
        <div class="additional-questions-popup__info-section">
          <h4 class="additional-questions-popup__section-title">Dane podstawowe</h4>
          <div class="additional-questions-popup__info-grid">
            <div class="additional-questions-popup__info-item">
              <span class="additional-questions-popup__label">Imię:</span>
              <span class="additional-questions-popup__value">{{ guest.name }}</span>
            </div>
            <div class="additional-questions-popup__info-item">
              <span class="additional-questions-popup__label">Nazwisko:</span>
              <span class="additional-questions-popup__value">{{ guest.surname }}</span>
            </div>
            <div class="additional-questions-popup__info-item">
              <span class="additional-questions-popup__label">Status:</span>
              <div
                class="additional-questions-popup__status"
                :class="getStatusClass(guest.presence)"
              >
                <i :class="getStatusIcon(guest.presence)"></i>
                <span>{{ getStatusText(guest.presence) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="additional-questions-popup__info-section">
          <h4 class="additional-questions-popup__section-title">Dodatkowe pytania</h4>
          <div
            v-if="questionsWithAnswers.length > 0"
            class="additional-questions-popup__questions"
          >
            <div
              v-for="question in questionsWithAnswers"
              :key="question.id"
              class="additional-questions-popup__question"
            >
              <div class="additional-questions-popup__question-header">
                <div class="additional-questions-popup__question-number">
                  <i class="pi pi-question-circle"></i>
                  <span>Pytanie {{ question.id }}</span>
                </div>
              </div>
              <div class="additional-questions-popup__question-content">
                <div class="additional-questions-popup__answer-container">
                  <i class="pi pi-comment"></i>
                  <span class="additional-questions-popup__answer">{{ formatAnswer(question.answer) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="additional-questions-popup__no-questions"
          >
            <i class="pi pi-info-circle"></i>
            <span>Brak dodatkowych pytań</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.additional-questions-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba($black, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fade-in 0.3s ease-out;

  &__content {
    background-color: $white;
    padding: 2.5rem;
    border-radius: 16px;
    max-width: 700px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba($black, 0.2);
    position: relative;
    animation: slide-up 0.3s ease-out;
    scrollbar-width: thin;
    scrollbar-color: $brown-300 $white;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: $white;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $brown-300;
      border-radius: 4px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2.5rem;
    position: sticky;
    top: 0;
    background-color: $white;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid $brown-600;
    z-index: 1;
  }

  &__title {
    color: $black-300;
    font-size: 2.72rem;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  &__close-button {
    background: none;
    border: none;
    color: $brown-200;
    cursor: pointer;
    padding: 0.75rem;
    font-size: 2.4rem;
    transition: all 0.2s ease;
    border-radius: 50%;
    width: 3.2rem;
    height: 3.2rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $brown-100;
      background-color: $brown-600;
      transform: rotate(90deg);
    }

    &:focus {
      outline: 2px solid $brown-200;
      outline-offset: 2px;
    }
  }

  &__guest-info {
    display: flex;
    flex-direction: column;
    gap: 3rem;
  }

  &__info-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__section-title {
    color: $black-300;
    font-size: 2.18rem;
    margin: 0;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid $brown-600;
    font-weight: 600;
    letter-spacing: -0.5px;
  }

  &__info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    padding: 0.5rem;
  }

  &__info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    background-color: $brown-600;
    border-radius: 12px;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  &__label {
    font-weight: 600;
    color: $brown-100;
    font-size: 1.5rem;
  }

  &__value {
    color: $black-300;
    font-size: 1.77rem;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1.63rem;

    i {
      font-size: 2.04rem;
    }

    &--confirmed {
      background-color: $status-green-light;
      color: $status-green;
    }

    &--declined {
      background-color: $status-red-light;
      color: $status-red;
    }

    &--pending {
      background-color: $status-yellow-light;
      color: $status-yellow;
    }
  }

  &__questions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  &__question {
    display: flex;
    flex-direction: column;
    background-color: $white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba($black, 0.08);
    transition: all 0.3s ease;
    border: 1px solid $brown-300;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba($black, 0.12);
    }
  }

  &__question-header {
    background-color: $brown-600;
    padding: 1.25rem;
    border-bottom: 1px solid $brown-300;
  }

  &__question-number {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: $brown-100;
    font-weight: 600;
    font-size: 1.77rem;

    i {
      color: $brown-200;
      font-size: 1.9rem;
    }
  }

  &__question-content {
    padding: 1.5rem;
  }

  &__answer-container {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    color: $black-300;
    line-height: 1.6;
    font-size: 1.63rem;

    i {
      color: $brown-200;
      font-size: 1.9rem;
      margin-top: 0.2rem;
    }
  }

  &__answer {
    flex: 1;
    overflow-wrap: break-word;
    font-size: 1.63rem;
  }

  &__no-questions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.25rem;
    color: $brown-100;
    font-style: italic;
    padding: 3rem;
    background-color: $white;
    border-radius: 16px;
    text-align: center;
    border: 2px dashed $brown-300;
    box-shadow: 0 4px 12px rgba($black, 0.05);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.02);
      box-shadow: 0 6px 16px rgba($black, 0.08);
    }

    i {
      color: $brown-200;
      font-size: 4.08rem;
    }

    span {
      font-size: 1.9rem;
    }
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: $media-max-phone) {
  .additional-questions-popup {
    &__content {
      padding: 1.5rem;
      width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
    }

    &__info-grid {
      grid-template-columns: 1fr;
    }

    &__questions {
      grid-template-columns: 1fr;
    }
  }
}
</style>
